import { cn, constants, phoneUtils } from "@/utils";
import { cnpjUtils } from "@/utils/cnpjUtils";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import CurrencyInput from "react-currency-input-field";
import type { IconType } from "../icon/icon";
import { Icon } from "../icon/icon";

export type InputType =
  | "default"
  | "phone"
  | "selectable"
  | "date"
  | "currency"
  | "percentage"
  | "cnpj"
  | "number";

export type InputProps = React.ComponentProps<"input"> & {
  label?: string;
  inputType?: InputType;
  errorMessage?: string;
  leftIcon?: IconType;
  rightComponent?: React.ReactNode;
  inputClassName?: string;
  selectableText?: string;
  allowNegative?: boolean;
  onClick?: () => void;
};

export const Input = ({
  label,
  inputType = "default",
  className,
  errorMessage,
  leftIcon,
  rightComponent,
  inputClassName,
  selectableText,
  allowNegative,
  onClick,
  onChange,
  value,
  type,
  ...inputProps
}: InputProps) => {
  const isPassword = type === "password";
  const [showPassword, setShowPassword] = useState(false);
  // Senha alterna entre oculta/visível pelo ícone de olho; demais inputs
  // mantêm o `type` recebido (padrão "text").
  const resolvedType = isPassword
    ? showPassword
      ? "text"
      : "password"
    : (type ?? "text");
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNumbers = event.target.value.replace(/\D/g, "");

    if (inputType === "number") {
      if (allowNegative) {
        event.target.value = event.target.value.replace(/[^0-9-]/g, "").replace(/(?!^)-/g, "");
      } else {
        event.target.value = onlyNumbers;
      }
    }

    if (inputType === "phone") {
      const formattedValue = phoneUtils.formatPhone(onlyNumbers);
      event.target.value = formattedValue;
    }

    if (inputType === "cnpj") {
      const formattedValue = cnpjUtils.formatCNPJ(onlyNumbers);
      event.target.value = formattedValue;
    }

    if (inputType === "percentage") {
      const rawValue = event.target.value.replace(",", ".");
      const numericValue = parseFloat(rawValue);

      if (!isNaN(numericValue)) {
        onChange?.({
          ...event,
          target: {
            ...event.target,
            value: String(numericValue),
          },
        });
      } else {
        onChange?.({
          ...event,
          target: {
            ...event.target,
            value: "0",
          },
        });
      }
      return;
    }

    onChange?.(event);
  };

  const handleCurrencyChange = (value: string | undefined) => {
    if (onChange) {
      if (inputProps.max && value) {
        const numericValue = parseFloat(value.replace(",", "."));
        if (numericValue > Number(inputProps.max)) {
          value = String(inputProps.max);
        }
        if (numericValue < 0) {
          value = "0";
        }
      }

      if (inputProps.min && value) {
        const numericValue = parseFloat(value.replace(",", "."));
        if (numericValue < Number(inputProps.min)) {
          value = String(inputProps.min);
        }
        if (numericValue < 0) {
          value = "0";
        }
      }

      const event = {
        target: {
          value: value || "0",
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(event);
    }
  };

  const isSelectable = inputType === "selectable" || inputType === "date";

  const currencyInputProps = {
    id: "input",
    name: "input-name",
    value: value as string,
    onValueChange: handleCurrencyChange,
    decimalsLimit: 2,
    decimalSeparator: ",",
    groupSeparator: ".",
    prefix: "R$ ",
    className: cn("flex flex-1 disabled:cursor-not-allowed", inputClassName),
    placeholder: "R$ 0,00",
    onKeyDown: inputProps.onKeyDown,
  };

  const percentageInputProps = {
    id: "input",
    name: "input-name",
    value: value as string,
    onValueChange: handleCurrencyChange,
    decimalsLimit: 2,
    decimalSeparator: ",",
    groupSeparator: ".",
    suffix: "%",
    className: cn("flex flex-1 disabled:cursor-not-allowed", inputClassName),
    placeholder: "0,00%",
    onKeyDown: inputProps.onKeyDown,
  };

  return (
    <div className={cn("relative flex flex-col gap-1", className)}>
      {label && (
        <label htmlFor="input" className="text-sm text-black">
          {label}
        </label>
      )}
      <div
        className={cn(
          "flex h-[41px] w-full items-center gap-2 rounded-sm border border-gray-200 p-2",
          // Campo bloqueado sinaliza pelo cursor, não só pelo texto cinza —
          // no container também, para cobrir a borda e os ícones, onde o
          // cursor do <input> não chega.
          inputProps.disabled && "cursor-not-allowed"
        )}
        onClick={onClick}
      >
        {leftIcon && <Icon icon={leftIcon} />}
        {inputType === "currency" ? (
          <CurrencyInput
            disabled={inputProps.disabled}
            {...currencyInputProps}
          />
        ) : inputType === "percentage" ? (
          <CurrencyInput
            disabled={inputProps.disabled}
            {...percentageInputProps}
          />
        ) : isSelectable ? (
          <span className="flex flex-1 text-black">{value}</span>
        ) : (
          <input
            id="input"
            type={resolvedType}
            inputMode={inputType === "phone" ? "tel" : "text"}
            onChange={handleChange}
            value={value}
            maxLength={
              inputType === "phone"
                ? constants.MAX_LENGTH_PHONE
                : inputType === "cnpj"
                  ? constants.MAX_LENGTH_CNPJ
                  : undefined
            }
            className={cn(
              "flex w-full min-w-0 flex-1 text-black placeholder:text-gray-500 disabled:cursor-not-allowed",
              inputClassName,
              {
                "text-gray-500": inputProps.disabled,
              }
            )}
            {...inputProps}
          />
        )}
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(prev => !prev)}
            disabled={inputProps.disabled}
            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            className="flex shrink-0 cursor-pointer items-center justify-center text-gray-500 transition-colors hover:text-black disabled:cursor-not-allowed"
          >
            {showPassword ? (
              <EyeOff className="size-[18px]" />
            ) : (
              <Eye className="size-[18px]" />
            )}
          </button>
        )}
        {rightComponent && rightComponent}
        {isSelectable && (
          <button
            type="button"
            onClick={onClick}
            disabled={inputProps.disabled}
            className="flex cursor-pointer items-center justify-center rounded-sm bg-gray-200 px-[6px] py-1 text-sm text-gray-600 disabled:cursor-not-allowed"
          >
            {selectableText ? selectableText : "Alterar"}
          </button>
        )}
      </div>
      {errorMessage && (
        <span className="text-xs text-[red]">{errorMessage}</span>
      )}
    </div>
  );
};
